import { Org, WaterExport, WaterExportInData, WaterExportsApi } from '@/api';
import { ConsentConditionWithParams, isConsentConditionWithParams } from '@/modules';
import { isArray } from '@amcharts/amcharts4/core';

type Override<T1, T2> = Omit<T1, keyof T2> & T2;
export type WaterExportWithTargetOrg = Override<
	WaterExport,
	{
		targetorg: Org;
	}
>;

export const WaterExportStatuses = ['enabled', 'disabled', 'internalOnly'] as const;
export type WaterExportStatus = typeof WaterExportStatuses[number];

export function isWaterExport(x: any): x is WaterExport {
	if (typeof x !== 'object') return false;
	if (typeof x.targetOrgId !== 'number') return false;
	if (typeof x.productId !== 'number') return false;
	if (isArray(x.waterMeterIds) === false) return false;
	for (const id of x.waterMeterIds) {
		if (typeof id !== 'number') {
			return false;
		}
	}
	return (
		typeof x.status === 'string' &&
		WaterExportStatuses.indexOf(x.status as WaterExportStatus) !== -1
	);
}

export class WaterExportModule {
	private api: WaterExportsApi;
	constructor() {
		this.api = new WaterExportsApi();
	}

	async get(waterExportId: number): Promise<WaterExport | undefined> {
		const res = await this.api.waterExportsWaterExportIdGet(waterExportId);

		if (res != undefined && res.data) {
			if (isWaterExport(res.data)) {
				return res.data;
			} else {
				throw Error(`Invalid water export from get from API. \n ${JSON.stringify(res)} `);
			}
		}

		return undefined;
	}

	async getWaterExportsForProduct(productId: number): Promise<WaterExportWithTargetOrg[]> {
		const res = await this.api.waterExportsGet({
			params: {
				'filter[productId]': productId,
				include: 'targetOrg,audits.user',
			},
		});

		if (res != undefined && res.data != undefined && res.data.data != undefined) {
			return <WaterExportWithTargetOrg[]>res.data.data;
		}
		return [];
	}

	async delete(waterExportId: number) {
		await this.api.deleteWaterExport(waterExportId);
	}

	async save(waterExport: WaterExport, waterExportId: number | undefined) {
		if (!isWaterExport(waterExport)) {
			throw Error(
				`Invalid water export with params for save via API. \n ${JSON.stringify(waterExport)} `,
			);
		}

		if (waterExportId != undefined) {
			return await this.api.updateWaterExport(waterExportId, waterExport);
		} else {
			return await this.api.createWaterExport(waterExport);
		}
	}
}
